import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { useCallback } from 'react';
import axios from '../axios';
import { getAuthProviderHeader } from './auth-provider-utils';

type TSendPasswordResetPayload = {
  email: string;
  redirectTo?: string;
};

export const useSendPasswordResetMutation = () => {
  return useMutation({
    mutationFn: async ({ email, redirectTo }: TSendPasswordResetPayload) => {
      const redirectUrl = redirectTo || `${window.location.origin}/auth/reset-password`;
      return axios.post('/auth/send-password-reset', { email, redirectUrl });
    },
    meta: {
      successMessage: 'Password reset email sent',
    },
    onError: (error: Error) => {
      const message =
        error instanceof AxiosError && error.response?.data?.message
          ? error.response.data.message
          : 'Failed to send password reset email';
      enqueueSnackbar(message, {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      });
    },
  });
};

type TPayload = {
  username: string;
  password: string;
};

export const useLogin = () => {
  const mutationFn = useCallback(async (payload: TPayload) => {
    // WordPress authentication flow (default)
    // Supabase login is handled directly in login pages when feature flag is enabled
    const response = axios.post('/auth/login', payload, {
      headers: getAuthProviderHeader(),
    });
    return response;
  }, []);

  return useMutation({
    mutationFn,
    onSuccess: async () => {
      enqueueSnackbar(`Login successful!`, {
        variant: 'success',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Unable to Login', {
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
      });
    },
  });
};

export const getValidateResetTokenQueryKeys = (token: string) => ['validate-reset-token', token];

export const useValidateResetTokenQuery = (token: string) => {
  return useQuery({
    queryKey: getValidateResetTokenQueryKeys(token),
    queryFn: async () => {
      const { data } = await axios.post<{ valid: boolean }>('/auth/validate-reset-token', { token });
      return data;
    },
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });
};

type TSetPasswordWithTokenPayload = {
  token: string;
  password: string;
};

export const useSetPasswordWithTokenMutation = (token: string) => {
  return useMutation({
    mutationFn: async ({ token, password }: TSetPasswordWithTokenPayload) => {
      return axios.post<{ email: string }>('/auth/set-password', { token, password });
    },
    meta: {
      errorMessage: 'Failed to reset password',
      invalidatesQuery: getValidateResetTokenQueryKeys(token),
    },
  });
};
